// Every board is a fixed 960×540, and Saga's text is as long as Saga makes
// it. The deck partials size type for the text they expect and split long
// lists across boards; this is the net under both. A `.deck-fit` box whose
// `.deck-fit-body` spills past it has that body zoomed down, one step at a
// time, until it fits — but never below the deck's 12px type floor at the
// 960 reference. Zoom rather than font-size, so every inline size in the
// body scales together, and the body is widened to match so the text reflows
// into the room instead of shrinking into a narrower column.
//
// Built as its own bundle too, so the PDF layout — which loads no app
// JavaScript — fits its boards before they print.

const TYPE_FLOOR = 12;
const STEP = 0.04;

function smallestType(body) {
  let smallest = Infinity;
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.textContent.trim() || !node.parentElement) continue;
    smallest = Math.min(smallest, parseFloat(getComputedStyle(node.parentElement).fontSize));
  }
  return smallest;
}

function spills(box) {
  return box.scrollHeight > box.clientHeight + 1 || box.scrollWidth > box.clientWidth + 1;
}

function fit(box) {
  const body = box.querySelector(":scope > .deck-fit-body");
  if (!body) return;

  body.style.zoom = "";
  body.style.width = "";
  if (!spills(box)) return;

  // A board's back is read up close, not across a room, and may go below
  // the floor it names (data-fit-floor).
  const floorPx = parseFloat(box.dataset.fitFloor) || TYPE_FLOOR;
  const floor = Math.min(1, floorPx / smallestType(body));
  const widen = box.dataset.fitWiden !== "false";
  let zoom = 1;
  while (spills(box) && zoom - STEP >= floor) {
    zoom -= STEP;
    body.style.zoom = zoom.toFixed(3);
    if (widen) body.style.width = `${(100 / zoom).toFixed(3)}%`;
  }
}

// Type is set smaller than the sizes the boards are drawn at: the deck's
// `--deck-type-scale` (every theme, KBA 17 Oct: "make all large text 10%
// smaller") times a theme's own `--deck-theme-type-scale` (Editorial V.2,
// 16 Oct: "scale all fonts down at least 10%"). Every size on a board —
// from the stylesheet, an inline style or an SVG attribute — is scaled
// once, with its leading, and never below the floor. Every board's sizes
// are read before any is set: a size inherited from a scaled parent is not
// scaled twice, and the page's styles are worked out once, not once a
// board.
function ownsText(element) {
  if (element instanceof SVGTextElement) return true;
  return Array.from(element.childNodes).some((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
}

function typeScale(board) {
  const style = getComputedStyle(board);
  const factor = (name) => {
    const value = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) && value > 0 ? value : 1;
  };
  return factor("--deck-type-scale") * factor("--deck-theme-type-scale");
}

export function scaleType(root = document) {
  const pending = [];
  root.querySelectorAll(".deck-board").forEach((board) => {
    if (board.dataset.typeScaled) return;
    const scale = typeScale(board);
    if (scale === 1) return;

    const sizes = [];
    board.querySelectorAll("*").forEach((element) => {
      if (!ownsText(element)) return;
      const style = getComputedStyle(element);
      sizes.push([element, parseFloat(style.fontSize), parseFloat(style.lineHeight)]);
    });
    pending.push([board, scale, sizes]);
  });

  pending.forEach(([board, scale, sizes]) => {
    sizes.forEach(([element, size, leading]) => {
      if (!(size > TYPE_FLOOR)) return;
      const scaled = Math.max(TYPE_FLOOR, size * scale);
      element.style.fontSize = `${scaled.toFixed(2)}px`;
      if (leading) element.style.lineHeight = `${(leading * scaled / size).toFixed(2)}px`;
    });
    board.dataset.typeScaled = "true";
  });
}

export function fitDeck(root = document) {
  scaleType(root);
  root.querySelectorAll(".deck-fit").forEach(fit);
}

function fitWhenReady() {
  fitDeck();
  // Metrics change when the deck's face arrives; fit again against it.
  document.fonts?.ready.then(() => fitDeck());
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", fitWhenReady, { once: true });
} else {
  fitWhenReady();
}
document.addEventListener("turbo:load", fitWhenReady);
// A cover drawn into a lazy frame on the Reports page arrives after load.
document.addEventListener("turbo:frame-load", (event) => fitDeck(event.target));
// A face hidden at load — a board's back — measures nothing until it is
// shown, so whatever shows it asks for a fit then.
document.addEventListener("deck:fit", (event) => fitDeck(event.target));
